import React, { useState, useEffect } from "react";
import { Card, Table } from "antd";
import { useSelector } from "react-redux";
import http from "../../../../services/http";
import { BeatLoader } from "react-spinners";
import { SubscriptionCardSkeleton, TableSkeleton } from "../../../../components/page-skeleton";
import CreditCard from "../../../../assets/images/credit-card.png";
import { toast } from "react-toastify";
import { ErrorOutline, Launch } from "@mui/icons-material";
import moment from "moment";
import "./styles.scss";
import SubscriptionModal from "../../../../components/subscription-modal";
import SubscribePlanCard from "../../../../components/subscribe-plan-card";
import { ENTERPRISE_ROLE } from "../../../../constant";

function Subscription() {
  // STATES
  const [isLoading, setLoading] = useState(true);
  const [subscriptionDetail, setSubscriptionDetail] = useState(null);
  const [downloadLoader, setdownloadLoader] = useState([]);
  const [manageLoader, setmanageLoader] = useState(false);
  const [isError, setError] = useState(false);
  const [subscriptionPreview, setSubscriptionPreview] = useState(false);

  // REDUX DATA
  const user = useSelector((state) => state.UserReducer.profile);
  const profileReady = Boolean(user?.id || user?.email);
  const isEnterpriseUser = user?.role === ENTERPRISE_ROLE;
  const isSubscribed = Boolean(user?.is_subscribed);
  const showUpgradeCard = profileReady && !isSubscribed && !isEnterpriseUser;

  // HANDLERS
  const manageSubscription = () => {
    if (manageLoader) return;
    setmanageLoader(true);
    http
      .get("subscription-session")
      .finally(() => setmanageLoader(false))
      .then(({ data }) => {
        window.location.href = data.url;
      })
      .catch(() => {});
  };
  const downloadPdf = async (invoice, name) => {
    setdownloadLoader((prev) => [...prev, invoice]);
    http
      .get(`/download-invoice/${invoice}`)
      .then((res) => {
        const link = document.createElement("a");
        link.href = res.data.invoice_pdf;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(({ response }) => {
        toast.error(response.data.error ?? "Something went wrong");
      })
      .finally(() => {
        setdownloadLoader((prev) => prev.filter((item) => item !== invoice));
      });
  };

  // CONST VALS
  const { subscription, subscription_expired } = subscriptionDetail || {};
  const { status, payment_status, cancelled, price, billing_cycle, next_billing_date } =
    subscription || {};
  const requiresPaymentUpdate = payment_status === "requires_payment_method";
  const paymentDueUpdate = status === "past_due";
  const nextBillingDate = moment
    .unix(next_billing_date ?? new Date())
    .format("MMMM DD, YYYY");
  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      key: "invoice_id",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (data) => moment.unix(data).format("DD MMM YYYY"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (data) => `${data} USD`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (data) => (
        <span className={`payment-status ${data === "paid" ? "paid" : ""}`}>{data}</span>
      ),
    },
    {
      title: "",
      dataIndex: "",
      key: "action",
      render: (data) => (
        <button
          className="download-invoice-btn"
          onClick={() => downloadPdf(data?.id, `invoice-${data?.number}.pdf`)}
        >
          {downloadLoader.includes(data?.id) ? (
            <BeatLoader size={7} color="#605bff" />
          ) : (
            "Download"
          )}
        </button>
      ),
    },
  ];

  // HELPERS
  const getSubscriptionHelper = async () => {
    http
      .get(`subscription`)
      .then(({ data }) => {
        setSubscriptionDetail(data);
      })
      .catch(({ response }) => setError(response.data.error ?? "Something went wrong"))
      .finally(() => setLoading(false));
  };

  // HOOKS
  useEffect(() => {
    if (!profileReady) return;
    if (showUpgradeCard) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getSubscriptionHelper();
  }, [profileReady, showUpgradeCard]);

  return (
    <div className="subscription-detail">
      <h2 className="page-title">Subscription</h2>
      {!profileReady || isLoading ? (
        <>
          <SubscriptionCardSkeleton />
          {!showUpgradeCard && (
            <Card className="card-box" style={{ marginTop: 16 }}>
              <TableSkeleton rows={6} />
            </Card>
          )}
        </>
      ) : showUpgradeCard ? (
        <SubscribePlanCard freeTrial={Boolean(user?.free_trial)} />
      ) : isError ? (
        <Card className="card-box loader">
          <h2>{isError}</h2>
        </Card>
      ) : (
        <>
          <Card className="card-box">
            <h2>Professional Account</h2>
            <h4>Monthly Plan</h4>

            {/* Primary Action Button */}
            <button
              className="manage-btn"
              onClick={
                subscription_expired
                  ? () => setSubscriptionPreview(true)
                  : manageSubscription
              }
            >
              {manageLoader ? (
                <BeatLoader size={8} color="#605bff" />
              ) : (
                <>
                  {subscription_expired
                    ? "Renew Subscription"
                    : requiresPaymentUpdate || paymentDueUpdate
                    ? "Update Payment Method"
                    : "Manage Plan"}
                  {!subscription_expired && <Launch />}
                </>
              )}
            </button>
            {subscription_expired ? (
              <h3>
                Your subscription has expired. Renew your subscription to access features.
              </h3>
            ) : requiresPaymentUpdate ? (
              <h3>
                <ErrorOutline />
                Your payment method is invalid. Please update your payment method to
                continue using access features.
              </h3>
            ) : paymentDueUpdate ? (
              <h3>
                <ErrorOutline />
                Your payment has failed. Please update your payment method to continue
                using access features.
              </h3>
            ) : cancelled ? (
              <h3>
                Your subscription has been canceled. However, you will retain access to
                all subscription features until {nextBillingDate}.
              </h3>
            ) : (
              <ul>
                <li>
                  Your upcoming payment of {price} USD will be charged on{" "}
                  {nextBillingDate}.
                </li>
                <li>
                  Your payment will be automatically renewed every{" "}
                  {billing_cycle === "year" ? "year" : "month"}.
                </li>
              </ul>
            )}
          </Card>
          {subscriptionDetail?.card && (
            <Card className="card-box">
              <h2>Payment Method</h2>
              <div className="card-detail">
                <img src={CreditCard} alt="" />
                <span>
                  <span>*** *** ***</span>
                  {subscriptionDetail?.card}
                </span>
              </div>
            </Card>
          )}
          <Card className="card-box">
            <h2>Invoicing</h2>
            <Table
              className="invoice-table"
              pagination={false}
              columns={columns}
              dataSource={subscriptionDetail?.invoices?.map((item, index) => ({
                ...item,
                key: index,
              }))}
            />
          </Card>
        </>
      )}
      <SubscriptionModal
        preview={subscriptionPreview}
        closeHandler={() => setSubscriptionPreview(false)}
      />
    </div>
  );
}

export default Subscription;
